/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AccountsApiService } from '../../modules/centralisedManagement/shared/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AdcPlatformModel, AdcServicesModel } from '../../modules/centralisedManagement/shared/models';
import { AfterViewChecked, Component, ComponentRef, ViewChild, ViewContainerRef } from '@angular/core';
import { BaseComponent, ProgressBarComponent } from '../../core/components';
import { DownloadEnum } from '../../modules/centralisedManagement/shared/enums';
import { DownloadService, LogService } from '../../core/services';
import { DropdownModel, ErrorResponseModel } from '../../core/models';
import { EnterprisePlatforms } from '../../modules/centralisedManagement/shared/lists';
import { HttpEventType } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';

declare let Intercom: any;

@Component({
    selector: 'app-free-trial',
    templateUrl: './free-trial.component.html',
    styleUrls: ['../externalPageStyles.scss', './free-trial.component.scss']
})
export class FreeTrialComponent extends BaseComponent implements AfterViewChecked {
    /**
     * Reference to new ADC container
     */
    @ViewChild('progressContainer', { read: ViewContainerRef })
    public progressContainer: any;

    /**
     * Store the current step.
     */
    public activeStep = 1;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        hypervisors: []
    };

    /**
     * The selected hypervisor.
     */
    public selectedHypervisor: AdcPlatformModel = null;

    /**
     * Enum reference for the view.
     */
    public adoptionEnum = DownloadEnum;

    /**
     * Set the progress state as the user selections options.
     */
    public progressState: DownloadEnum = DownloadEnum.WAITING;

    /**
     * Store the completed downloads.
     */
    public completedDownloads: string[] = [];

    /**
     * Boolean to show/hide calendly.
     */
    public hideCalendly: boolean = true;

    /**
     * Lookup intercom message object.
     */
    public intercomMessage = {
        general: 'I require help setting up my appliance. Can I speak to someone?',
        support: 'The hypervisor I want to use isn\'t in the list. Can I speak to someone?',
        extension: 'I require an extension of the Enterprise trial. Can I speak to sales?',
    };

    /**
     * User object collected from hubspot and passed to calendly.
     */
    public userObject: {
        name: string,
        email: string
    };

    /**
     * List of platform.
     * @private
     */
    private enterprisePlatforms: AdcPlatformModel[] = EnterprisePlatforms;

    /**
     * File location for downloads.
     * @private
     */
    private fileDownloadUrl: string = 'https://downloads.loadbalancer.org/releases/virtual/current/';

    /**
     * Modal Component Reference
     * @private
     */
    private componentReference: ComponentRef<any>;

    /**
     * Store the hubspot ID.
     * @private
     */
    private hubspotId: string = null;

    constructor(
        private accountsApiService: AccountsApiService,
        private activatedRoute: ActivatedRoute,
        private downloadService: DownloadService,
        private logService: LogService,
        private router: Router,
    ) {
        super();

        this.setupDropdown();
        this.getHubSpotDetails();
    }

    public ngAfterViewChecked(): void {
        this.replaceContactUsWithLink();
    }

    /**
     * Replace contact us with link.
     * @private
     */
    private replaceContactUsWithLink() {
        const contactUsLinks = document.querySelectorAll('.message-with-contact');
        for (const link of Array.from(contactUsLinks)) {
            const html = link.innerHTML;
            if (!html.includes('button-as-link')) {
                const newHTML = html.replace(
                    'contact us',
                    `<button class="button-as-link">
                    contact us
                    </button>`
                );
                link.addEventListener('click', () => this.openIntercom(this.getMessageByActiveStep()));
                link.innerHTML = newHTML;
            }

        }
    }

    /**
     * Get message for intercom by active step.
     * @private
     */
    private getMessageByActiveStep(): string {
        let type: string;
        switch (this.activeStep) {
            case 1:
                type = 'extension';
                break;
            case 2:
                type = 'support';
                break;
            default:
                type = 'general';
        }

        return this.intercomMessage[type];
    }

    /**
     * Move to the next step.
     */
    public nextStep(): void {
        this.activeStep++;
    }

    /**
     * Activate account with hubspot ID
     */
    public activateTrial(): void {
        const url = this.hubspotId ? '/activate-portal/' + this.hubspotId : '/register';
        this.router.navigate([url]).then().catch();
    }

    /**
     * Sore the active step number.
     * @param stepNumber
     */
    public setActiveStep(stepNumber: number): void {
        this.activeStep = stepNumber;
    }

    /**
     * CTA to open intercom chat.
     */
    public openIntercom(message: string): void {
        Intercom('showNewMessage', message);
    }

    /**
     * Store the chosen hypervisor.
     * @param $event
     */
    public chosenHypervisor($event: any): void {
        this.completedDownloads = [];
        this.selectedHypervisor = this.enterprisePlatforms.find(item => item.name === $event);
    }

    /**
     * Start download helper.
     * @param name
     */
    public startDownload(name: string): void {

        if (name === 'MD5' || name === 'SHA') {
            const service = this.selectedHypervisor.services.find(item => item.name === name);
            this.triggerSimpleDownload(service);
        } else {
            const service = this.selectedHypervisor.services[0];
            this.triggerDownload(service);
        }
    }

    /**
     * Show the calendly widget.
     */
    public loadCalendly(): void {
        this.hideCalendly = false;
    }

    /**
     * Hide the calendly widget.
     */
    public hideCalendlyWidget(): void {
        this.hideCalendly = true;
    }

    /**
     * Set up the dropdown content.
     * @private
     */
    private setupDropdown(): void {
        for (const hyper of this.enterprisePlatforms) {
            this.dropdown.hypervisors.push(<DropdownModel>{ key: hyper.name, value: hyper.title });
        }
    }

    /**
     * Trigger the download in the download component.
     * @param service
     */
    private triggerDownload(service: AdcServicesModel): void {
        this.progressState = DownloadEnum.DOWNLOADING;
        const progressComponent = this.progressContainer.createComponent(ProgressBarComponent);
        // Set value in the progress component.
        progressComponent.instance.fileUrl = this.fileDownloadUrl + service.fileName;
        progressComponent.instance.spinner = true;
        progressComponent.instance.onCompleteCallBack = this.cleanUpProgressComponent.bind(this, service);
        progressComponent.instance.onCancelCallBack = this.cancelDownload.bind(this);
        // Component reference for destroy later.
        this.componentReference = progressComponent;
    }

    /**
     * Clean up progress component.
     * @private
     */
    private cleanUpProgressComponent(service: any): void {
        this.completedDownload(service);
        this.progressState = DownloadEnum.DOWNLOADED;
        if (this.componentReference) {
            this.componentReference.destroy();
        }
    }

    /**
     * Cancel download.
     * @private
     */
    private cancelDownload(): void {
        this.progressState = DownloadEnum.WAITING;
        if (this.componentReference) {
            this.componentReference.destroy();
        }
    }

    /**
     * Trigger a simple download.
     * @param service
     */
    private triggerSimpleDownload(service: AdcServicesModel): void {
        const fileUrl = this.fileDownloadUrl + service.fileName;
        this.downloadService.download(fileUrl, 'blob')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (result: any): void => {
                    if (result.type === HttpEventType.Response) {
                        this.downloadService.generateDownload(
                            result.body,
                            fileUrl,
                            { callback: this.completedDownload(service) }
                        );
                    }
                },
                error: (error: any): void => {
                    this.logService.error('AddLoadbalancerComponent: triggerSimpleDownload', error);
                }
            });
    }

    /**
     * Store completed downloads.
     * @param service
     * @private
     */
    private completedDownload(service: AdcServicesModel): any {
        this.completedDownloads.push(service.name);
    }

    /**
     * Get details from hubspot.
     * @private
     */
    private getHubSpotDetails(): void {
        const hubspotId = this.activatedRoute.snapshot.paramMap.get('hubspotId');
        if (hubspotId) {
            this.accountsApiService.getHubspotAccount(hubspotId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: { firstname: string, lastname: string, email: string }): void => {
                        this.hubspotId = hubspotId;
                        this.userObject = {
                            name: `${data.firstname} ${data.lastname}`,
                            email: data.email
                        };
                    },
                    error: (error: ErrorResponseModel) => {
                        this.userObject = {
                            name: undefined,
                            email: undefined
                        };
                        this.hubspotId = null;
                        this.logService.error('ActivateAccountComponent:activateAccount', error.message);
                    }
                });
        }
    }
}
